// Client-side image prep for photo uploads.
//
// HEIC handling: iPhones (Safari) capture image/heic by default. The server does NOT
// decode HEIC (pure-Go, no libheif). On mobile we transcode HEIC -> JPEG on-device via
// canvas before upload, so the server only ever receives JPEG. Safari natively decodes
// HEIC, which is the only browser that emits it, so canvas suffices — no WASM decoder.
// The server still authoritatively strips metadata by re-encoding; this is just to make
// the format acceptable and shrink the upload.

const MAX_UPLOAD_BYTES = 15 * 1024 * 1024; // matches server cap

// isHeic detects HEIC/HEIF by MIME or extension (some browsers report an empty type).
function isHeic(file) {
  const t = (file.type || "").toLowerCase();
  if (t.includes("heic") || t.includes("heif")) return true;
  return /\.(heic|heif)$/i.test(file.name || "");
}

// transcodeToJpeg draws a File to a canvas and re-encodes as JPEG. Works only where the
// browser can decode the source (Safari for HEIC). Throws on decode failure.
function transcodeToJpeg(file, quality = 0.9) {
  return new Promise((resolve, reject) => {
    const url = URL.createObjectURL(file);
    const img = new Image();
    img.onload = () => {
      URL.revokeObjectURL(url);
      const canvas = document.createElement("canvas");
      canvas.width = img.naturalWidth;
      canvas.height = img.naturalHeight;
      const ctx = canvas.getContext("2d");
      ctx.drawImage(img, 0, 0);
      canvas.toBlob(
        (blob) => {
          if (!blob) {
            reject(new Error("could not encode image"));
            return;
          }
          const name = (file.name || "photo").replace(/\.(heic|heif)$/i, "") + ".jpg";
          resolve(new File([blob], name, { type: "image/jpeg" }));
        },
        "image/jpeg",
        quality
      );
    };
    img.onerror = () => {
      URL.revokeObjectURL(url);
      reject(new Error("could not decode image in this browser"));
    };
    img.src = url;
  });
}

// prepareForUpload returns a File ready to POST: HEIC transcoded to JPEG, others passed
// through. Throws with a user-facing message on unsupported HEIC or oversize.
export async function prepareForUpload(file) {
  let out = file;
  if (isHeic(file)) {
    try {
      out = await transcodeToJpeg(file);
    } catch {
      throw new Error(
        "This looks like a HEIC photo your browser can't convert. Please upload a JPEG or PNG."
      );
    }
  }
  if (out.size > MAX_UPLOAD_BYTES) {
    throw new Error("Photo is larger than 15 MB. Please choose a smaller image.");
  }
  return out;
}

// isTouchDevice picks the file <input accept>: mobile gets the camera + HEIC (which we
// transcode); desktop is restricted to JPEG/PNG so unconvertible HEIC can't be selected.
export function isTouchDevice() {
  if (typeof window === "undefined") return false;
  return window.matchMedia?.("(pointer: coarse)").matches ?? "ontouchstart" in window;
}
